import { Alert, Box, Button, Card, CardContent, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import CSVUpload from "./CSVUpload";
import React from "react";
import CloseIcon from '@mui/icons-material/Close';
import { grey, red } from '@mui/material/colors';
import { CreateGroupsInClass } from "../FirebaseQueries";
import { Error } from "@mui/icons-material";

interface IGroupData {
	id: string;
	groupNumber: number;
	assignedPlayers: string[];
}

interface Student {
	id: string;
	email: string;
	fullName: string;
}

interface Group {
	number: number;
	students: Student[];
	isInvalid: boolean;
	isInUse: boolean;
	errors: Array<string>;
}

interface Props {
	isOpen: boolean;
	onClose: () => void;
	classId: string;
	scenarioIndex: number;
	allStudents: Student[];
	otherGroups: IGroupData[];
}


export default function ImportClassGroupsDialog(props: Props) {
	const { isOpen, onClose, classId, scenarioIndex, allStudents, otherGroups } = props;
	const [uploadData, setUploadData] = React.useState<Array<Group>>([]);
	const [fileName, setFileName] = React.useState<string>("");
	const [uploadErrors, setUploadErrors] = React.useState<Array<string>>([]);
	const [errorCreatingGroups, setErrorCreatingGroups] = React.useState(false);

	const HandleCSVUpload = (valueFileName: string, value: Array<Array<string>>) => {
		let groupIndex: number = value[0].indexOf("group");
		let emailIndex: number = value[0].indexOf("email");
		let groupsMap: Map<string, Group> = new Map();
		setFileName(valueFileName);

		if (groupIndex === -1) setUploadErrors(["Header item \"group\" not found.", ...uploadErrors]);
		if (emailIndex === -1) setUploadErrors(["Header item \"email\" not found.", ...uploadErrors]);

		if (groupIndex !== -1 && emailIndex !== -1) {
			// index starts at 1 because the first row is the headers
			// every row is another student email with repeated group values
			for (let i = 1; i < value.length; i++) {
				if (value[i].length === 1) continue;

				const groupNum = value[i][groupIndex];
				const email = value[i][emailIndex];
				let isGroupInvalid: boolean = !Number.isInteger(Number(groupNum)) || Number(groupNum) < 0;
				let isGroupInUse: boolean = otherGroups.some((group) => group.groupNumber === Number(groupNum));

				let editGroup = groupsMap.get(groupNum);

				if (editGroup === undefined) {
					groupsMap.set(groupNum, {
						number: Number(groupNum),
						students: [{
							id: "",
							fullName: "",
							email: email
						}],
						isInvalid: isGroupInvalid,
						isInUse: isGroupInUse,
						errors: []
					});
				} else {
					// add student to map value
					editGroup.students.push({
						id: "",
						fullName: "",
						email: email
					});
					editGroup.isInvalid = isGroupInvalid ? true : editGroup.isInvalid;
					editGroup.isInUse = isGroupInUse ? true : editGroup.isInUse;
					groupsMap.set(groupNum, editGroup);
				}
			}
			// check for errors
			groupsMap.forEach(group => {
				if (group.isInvalid) {
					group.errors.push("Group Number is not valid.");
				}
				if (group.isInUse) {
					group.errors.push("Group Number is already in use.");
				}

				group.students.forEach(student => {
					let studentData = allStudents.find((allStudent) => allStudent.email === student.email);
					if (studentData === undefined) {
						// student email not found
						group.errors.push(`(${student.email}) was not found.`);
					} else {
						// if found, populate data
						student.id = studentData.id;
						student.fullName = studentData.fullName;

						let otherGroup = otherGroups.find((group) => group.assignedPlayers.some((id) => id === studentData.id));
						if (otherGroup !== undefined) {
							// student is in another group
							group.errors.push(`(${student.email}) is already in group ${otherGroup.groupNumber}.`);
						}
					}
				})
			});

			setUploadData(Array.from(groupsMap.values()));
		}
	}

	const handleOnCreateGroups = async () => {
		try {
			let uploadGroups = uploadData.map((data) => ({
				number: data.number,
				studentIds: data.students.map((student) => student.id)
			}))
			await CreateGroupsInClass(classId, scenarioIndex, uploadGroups)
			onClose();
		} catch (err) {
			setErrorCreatingGroups(true);
			onClose();
			console.log(err);
		}
	}

	const clearCSV = () => {
		setUploadData([]);
		setUploadErrors([]);
	}

	const closeDialog = () => {
		clearCSV();
		onClose();
	}

	return (
		<>
			<Dialog onClose={onClose} open={isOpen}>
				<DialogTitle>Import Class Groups From CSV</DialogTitle>
				<DialogContent sx={{ minWidth: "500px" }}>
					{uploadData.length === 0 && uploadErrors.length === 0 ?
						<Stack spacing={4}>
							<CSVUpload valueChanged={HandleCSVUpload}></CSVUpload>
							<Stack spacing={1} sx={{ marginTop: 2, maxWidth: "300px" }}>
								<Typography fontWeight={"bold"}>CSV Format:</Typography>
								<Grid container>
									<Grid size={6}>
										<Typography>group</Typography>
										<Typography>1</Typography>
										<Typography>1</Typography>
										<Typography>1</Typography>
									</Grid>
									<Grid size={6}>
										<Typography>email</Typography>
										<Typography>example1@email.com</Typography>
										<Typography>example2@email.com</Typography>
										<Typography>example3@email.com</Typography>
									</Grid>
								</Grid>
							</Stack>
						</Stack>
						:
						<Stack spacing={2}>
							<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
								<Box>
									<Typography>Reviewing file: {fileName}</Typography>
								</Box>
								<IconButton
									aria-label="close"
									color="inherit"
									size="small"
									onClick={() => {
										clearCSV();
									}}
								>
									<CloseIcon fontSize="inherit" />
								</IconButton>
							</Stack>

							{uploadErrors.length > 0 ?
								<Stack sx={{ backgroundColor: red[100], padding: 1, borderRadius: 1, marginTop: 2 }}>
									<Typography color={red[700]} fontWeight={"bold"}>Errors</Typography>
									<Divider sx={{ backgroundColor: red[700] }}></Divider>
									<Stack sx={{ marginTop: 1 }}>
										{uploadErrors.map((error, index) => (
											<Typography color={red[700]} key={index}>{error}</Typography>
										))}
									</Stack>
								</Stack>
								:
								<></>
							}

							{uploadErrors.length > 0 || uploadData.some((item) => item.errors.length > 0) ?
								<Typography fontWeight={"bold"}>Fix errors, and try uploading again.</Typography>
								:
								<Typography>Confirm to create <span style={{ fontWeight: "bold" }}>{uploadData.length}</span> group(s).</Typography>
							}

							{uploadData.map((item, index) => (
								<Card key={index} sx={{ borderWidth: 2 }}>
									<Typography fontSize={"18px"} fontWeight={"bold"}>{item.number}</Typography>
									<CardContent sx={{ marginTop: 2 }}>
										<Typography>Student(s)</Typography>
										<Divider></Divider>
										<Stack spacing={0.1} sx={{ marginTop: 1 }}>
											{item.students.map((s, index) => (
												<Stack key={index} direction={"row"} spacing={0.5}>
													{s.fullName !== "" ? <Typography>{s.fullName}</Typography> : <Typography>[Not Found]</Typography>}
													<Typography color={grey[600]}>({s.email})</Typography>
												</Stack>
											))}
										</Stack>
										{item.errors.length > 0 ?
											<Box sx={{ backgroundColor: red[100], padding: 1, borderRadius: 1, marginTop: 2 }}>
												<Typography color={red[700]} fontWeight={"bold"}>Error(s)</Typography>
												<Divider sx={{ backgroundColor: red[700] }}></Divider>
												<Stack sx={{ marginTop: 1 }}>
													{item.errors.map((e, index) => (
														<Typography color={red[700]} key={index}>{e}</Typography>
													))}
												</Stack>
											</Box>
											:
											<></>
										}
									</CardContent>
								</Card>
							))}
						</Stack>
					}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDialog}>Cancel</Button>
					<Button
						disabled={uploadErrors.length > 0 || uploadData.length === 0 || uploadData.some((item) => item.errors.length > 0)}
						onClick={handleOnCreateGroups}>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>
			<Collapse in={errorCreatingGroups} mountOnEnter unmountOnExit>
				<Alert icon={<Error fontSize="inherit" />} severity="error"
					action={
						<IconButton
							aria-label="close"
							color="inherit"
							size="small"
							onClick={() => {
								setErrorCreatingGroups(false);
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}>
					There was an error creating the groups. Please try again.
				</Alert>
			</Collapse>
		</>
	)
}